import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useIntl } from 'react-intl';
import apiCalls from '../../utils/apiCalls';

export function useDeliveryDays(params) {
  const { data, ...rest } = useQuery(['deliveryDays', params], () =>
    apiCalls.getDeliveryDays(params).then(({ data: { deliveryDays } }) => deliveryDays)
  );
  return { deliveryDays: data, ...rest };
}

export function useRoutePlanSetups({ deliveryDayId }) {
  const { data, ...rest } = useQuery(
    ['routePlanSetups', { deliveryDayId }],
    () => apiCalls.getRoutePlanSetups({ deliveryDayId }).then(({ data: { routePlanSetups } }) => routePlanSetups),
    { enabled: !!deliveryDayId }
  );
  return { routePlanSetups: data, ...rest };
}

export function useStartDepots() {
  const { data, ...rest } = useQuery(['startDepots'], () =>
    apiCalls.getStartDepots().then(({ data: { startDepots } }) => startDepots)
  );
  return { startDepots: data, ...rest };
}

export function useDeliveryOrderBatches({ deliveryDayTimeRangeId, deliveryDayId }) {
  const { data, ...rest } = useQuery(
    ['deliveryOrderBatches', { deliveryDayTimeRangeId, deliveryDayId }],
    () =>
      apiCalls
        .getDeliveryOrderBatches({ deliveryDayTimeRangeId, deliveryDayId })
        .then(({ data: { deliveryOrderBatches } }) => deliveryOrderBatches),
    { enabled: !!deliveryDayTimeRangeId || !!deliveryDayId }
  );
  return { deliveryOrderBatches: data, ...rest };
}

export function useDeliveryOrderBatchesWithoutDeliveries({ deliveryDayId }) {
  const { data, ...rest } = useQuery(
    ['deliveryOrderBatchesWithoutDeliveries', { deliveryDayId }],
    () =>
      apiCalls
        .getDeliveryOrderBatches({ deliveryDayId, findWithoutDeliveries: true })
        .then(({ data: { deliveryOrderBatches } }) => deliveryOrderBatches),
    { enabled: typeof deliveryDayId === 'number' }
  );
  return { deliveryOrderBatches: data, ...rest };
}

export function useUsers({ userIds }) {
  const { data, ...rest } = useQuery(
    ['users', { userIds }],
    () =>
      apiCalls
        .getUsersAdmin({
          userIds: userIds.join(','),
        })
        .then(({ data: { users } }) => users),
    { enabled: !!userIds && !!userIds.length }
  );
  return { users: data, ...rest };
}

export function useRoutePlans({ deliveryDayId }) {
  const { data, ...rest } = useQuery(['routePlans', deliveryDayId], () =>
    apiCalls
      .getRoutePlans({
        deliveryDayId,
      })
      .then(({ data: { routePlans } }) => routePlans)
  );
  return { routePlans: data, ...rest };
}

export function useDeliveries({ query, deliveryDayId }) {
  const { data, ...rest } = useQuery(['deliveries', query], () =>
    apiCalls
      .getDeliveries({
        deliveryDayId,
        query,
      })
      .then(({ data: { deliveries } }) => deliveries)
  );
  return { deliveries: data, ...rest };
}

export function useRoutePlanSetupDeliveries({ routePlanSetupId, routesWithoutRoutePlan }) {
  const { data, ...rest } = useQuery(
    ['deliveries', { routePlanSetupId, routesWithoutRoutePlan }],
    () =>
      apiCalls
        .getDeliveries({
          routePlanSetupId,
          routesWithoutRoutePlan,
        })
        .then(({ data: { deliveries } }) => deliveries),
    {
      enabled: !!routePlanSetupId,
    }
  );
  return { deliveries: data, ...rest };
}

export function useDrivers() {
  const { data, ...rest } = useQuery(['drivers'], () =>
    apiCalls.getDrivers({}).then(({ data: { drivers } }) => drivers)
  );
  return { drivers: data, ...rest };
}

export function useRoutePlan({ deliveryDayTimeRangeId, routePlanSetupPickupSlotId }) {
  const { data, ...rest } = useQuery(
    ['routePlan', { deliveryDayTimeRangeId, routePlanSetupPickupSlotId }],
    () =>
      apiCalls
        .getRoutePlans({
          deliveryDayTimeRangeId,
          routePlanSetupPickupSlotId,
        })
        .then(({ data: { routePlans } }) => routePlans[0]),
    {
      enabled: !!deliveryDayTimeRangeId || !!routePlanSetupPickupSlotId,
    }
  );
  return { routePlan: data, ...rest };
}

export function useRoutes({ routePlanId, deliveryDayId, deliveryIds, showOrderInfo, refetchInterval, version }) {
  const queryClient = useQueryClient();

  const { data, ...rest } = useQuery(
    ['routes', { routePlanId, deliveryDayId }],
    () =>
      apiCalls
        .getRoutes({
          routePlanId,
          deliveryDayId,
          showOrderInfo,
          deliveryIds,
          version,
        })
        .then(({ data: { routes } }) => routes),
    {
      enabled: !!routePlanId || !!deliveryDayId,
      refetchInterval,
      onError: (error) => {
        queryClient.setQueryData(['routes', { routePlanId }], null);
      },
    }
  );
  return { routes: data, ...rest };
}

export function useRoute({ routeId, showOrderInfo, enabled }) {
  const { data, ...rest } = useQuery(
    ['route', { routeId, showOrderInfo }],
    () =>
      apiCalls
        .getRoutes({
          routeId,
          showOrderInfo,
        })
        .then(({ data: { routes } }) => routes[0]),
    {
      enabled,
    }
  );
  return { route: data, ...rest };
}

export function useRoutePlanCreation() {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ routePlan }) => apiCalls.createRoutePlan({ routePlan }).then(({ data: { routePlan } }) => routePlan),
    {
      onSuccess: (routePlan) => {
        const deliveryDayTimeRangeId = routePlan.deliveryDayTimeRange.id;
        queryClient.setQueryData(['routePlan', { deliveryDayTimeRangeId }], routePlan);
      },
    }
  );
  return { createRoutePlan: mutate, ...rest };
}

export function useRoutePlanEdit() {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ routePlan, params }) =>
      apiCalls.editRoutePlan({ routePlan, ...params }).then(({ data: { routePlan } }) => routePlan),
    {
      onSuccess: (routePlan) => {
        const deliveryDayTimeRangeId = routePlan.deliveryDayTimeRange?.id;
        const routePlanSetupPickupSlotId = routePlan.routePlanSetupPickupSlotId;
        queryClient.setQueryData(['routePlan', { deliveryDayTimeRangeId, routePlanSetupPickupSlotId }], routePlan);
        queryClient.invalidateQueries('routes');
      },
    }
  );
  return { editRoutePlan: mutate, ...rest };
}

export function useVehicleEdit(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ vehicle }) => apiCalls.editVehicle({ vehicle }).then(({ data: { vehicle } }) => vehicle),
    {
      onSuccess: (vehicle) => {
        queryClient.invalidateQueries('routePlan');
        onSuccess(vehicle);
      },
    }
  );
  return { editVehicle: mutate, ...rest };
}

export function useDeliveryDayEdit(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ deliveryDay, populateRoutePlanSetups }) =>
      apiCalls
        .editDeliveryDay({ deliveryDay, populateRoutePlanSetups })
        .then(({ data: { deliveryDay } }) => deliveryDay),
    {
      onSuccess: (deliveryDay) => {
        queryClient.invalidateQueries('deliveryDays');
        queryClient.invalidateQueries('routePlanSetups');
        onSuccess(deliveryDay);
      },
    }
  );
  return { editDeliveryDay: mutate, ...rest };
}

export function useRoutePlanSetupEdit(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ routePlanSetup, optimise, deleteRoutePlans, refreshRouteOptimisationStatus }) =>
      apiCalls
        .editRoutePlanSetup({ routePlanSetup, optimise, deleteRoutePlans, refreshRouteOptimisationStatus })
        .then(({ data: { routePlanSetup } }) => routePlanSetup),
    {
      onSuccess: (routePlanSetup) => {
        queryClient.invalidateQueries('routePlanSetups');
        queryClient.invalidateQueries('routePlan');
        onSuccess(routePlanSetup);
      },
    }
  );
  return { editRoutePlanSetup: mutate, ...rest };
}

export function useRouteEdit(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ route }) => apiCalls.editRoute({ route }).then(({ data: { route } }) => route),
    {
      onSuccess: (route) => {
        queryClient.invalidateQueries('routes');
        onSuccess(route);
      },
    }
  );
  return { editRoute: mutate, ...rest };
}

export function useDeliveryCreate(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ delivery }) => apiCalls.createDelivery({ delivery }).then(({ data: { delivery } }) => delivery),
    {
      onSuccess: (delivery) => {
        queryClient.invalidateQueries('routes');
        onSuccess(delivery);
      },
    }
  );
  return { createDelivery: mutate, ...rest };
}

export function useSmsCreate(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation((data) => apiCalls.createSms(data).then(({ data: { sms } }) => sms), {
    onSuccess: (sms) => {
      queryClient.invalidateQueries('routes');
      onSuccess(sms);
    },
  });
  return { createSms: mutate, ...rest };
}

export function useDeliveryEdit(onSuccess) {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(
    ({ delivery }) => apiCalls.editDelivery({ delivery }).then(({ data: { delivery } }) => delivery),
    {
      onSuccess: (delivery) => {
        queryClient.invalidateQueries('deliveries');
        queryClient.invalidateQueries('routes');
        onSuccess(delivery);
      },
    }
  );
  return { editDelivery: mutate, ...rest };
}

export function useDeliveryDelete() {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(({ deliveryId }) => apiCalls.deleteDelivery({ deliveryId }), {
    onSuccess: () => {
      queryClient.invalidateQueries('routes');
      queryClient.invalidateQueries('deliveryOrderBatchesWithoutDeliveries');
    },
  });
  return { deleteDelivery: mutate, ...rest };
}

export function useRoutePlanDelete() {
  const queryClient = useQueryClient();
  const { mutate, ...rest } = useMutation(({ routePlanId }) => apiCalls.deleteRoutePlan({ routePlanId }), {
    onSuccess: () => {
      queryClient.invalidateQueries('routePlan');
      queryClient.invalidateQueries('routes');
    },
  });
  return { deleteRoutePlan: mutate, ...rest };
}

export function useVehicleTypes() {
  const { formatMessage } = useIntl();
  const format = (id) => formatMessage({ id });
  return {
    isLoading: false,
    vehicleTypes: [
      {
        id: 'CAR_SMALL',
        capacity: 6,
        name: format('admin.vehicle.types.CAR_SMALL'),
      },
      {
        id: 'CAR_REGULAR',
        capacity: 10,
        name: format('admin.vehicle.types.CAR_REGULAR'),
      },
      {
        id: 'PANEL_VAN',
        capacity: 20,
        name: format('admin.vehicle.types.PANEL_VAN'),
      },
      {
        id: 'VAN',
        capacity: 40,
        name: format('admin.vehicle.types.VAN'),
      },
      {
        id: 'TRUCK_SMALL',
        capacity: 60,
        name: format('admin.vehicle.types.TRUCK_SMALL'),
      },
      {
        id: 'TRUCK_LARGE',
        capacity: 100,
        name: format('admin.vehicle.types.TRUCK_LARGE'),
      },
    ],
  };
}
